import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth.js';
import { useI18n } from '../lib/i18n.js';
import { ApiError } from '../lib/api.js';
import { Button, Field, inputClass } from '../components/ui.js';

type Mode = 'clinician' | 'parent';

export function Login() {
  const { login, requestOtp, verifyOtp } = useAuth();
  const { locale, setLocale, t } = useI18n();
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>('clinician');

  // clinician
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [mfaRequired, setMfaRequired] = useState(false);

  // parent
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);

  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [busy, setBusy] = useState(false);

  const fr = locale === 'fr';

  async function submitClinician(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await login(email, password, mfaRequired ? totp : undefined);
      navigate('/');
    } catch (err) {
      if (err instanceof ApiError && err.code === 'mfa_required') {
        setMfaRequired(true);
        setError(t('auth.mfaRequired'));
      } else {
        setError(t('auth.invalidCredentials'));
      }
    } finally {
      setBusy(false);
    }
  }

  async function sendCode(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    setInfo('');
    try {
      await requestOtp(phone);
      setCodeSent(true);
      setInfo(t('auth.codeSent'));
    } catch (err) {
      // A 429 means the per-minute send limit was hit — tell the parent to wait,
      // rather than implying the service is down.
      setError(err instanceof ApiError && err.status === 429 ? t('auth.tooManyAttempts') : t('auth.codeSendFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await verifyOtp(phone, code);
      navigate('/');
    } catch {
      setError(t('auth.invalidCode'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="bg-white p-8 rounded-xl shadow-md w-full max-w-sm border border-slate-200">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold text-brand-dark">ToxoFollow</h1>
          <button type="button" onClick={() => setLocale(fr ? 'en' : 'fr')} className="text-sm underline text-brand">
            {fr ? 'EN' : 'FR'}
          </button>
        </div>

        {/* Mode toggle */}
        <div className="flex rounded-lg border border-slate-200 overflow-hidden mb-5 text-sm">
          <button
            onClick={() => { setMode('parent'); setError(''); }}
            className={`flex-1 py-2 ${mode === 'parent' ? 'bg-brand text-white' : 'bg-white text-slate-600'}`}
          >
            {t('auth.loginBySms')}
          </button>
          <button
            onClick={() => { setMode('clinician'); setError(''); }}
            className={`flex-1 py-2 ${mode === 'clinician' ? 'bg-brand text-white' : 'bg-white text-slate-600'}`}
          >
            {t('auth.loginByPassword')}
          </button>
        </div>

        {mode === 'clinician' ? (
          <form onSubmit={submitClinician}>
            <Field label={t('auth.email')}>
              <input className={inputClass} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="username" />
            </Field>
            <Field label={t('auth.password')}>
              <input className={inputClass} type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" />
            </Field>
            {mfaRequired && (
              <Field label={t('auth.mfaCode')}>
                <input className={inputClass} value={totp} onChange={(e) => setTotp(e.target.value)} inputMode="numeric" />
              </Field>
            )}
            {error && <p className="text-danger text-sm mb-3">{error}</p>}
            <Button type="submit" disabled={busy} className="w-full">{busy ? '…' : t('auth.signIn')}</Button>
          </form>
        ) : (
          <form onSubmit={codeSent ? submitCode : sendCode}>
            <p className="text-sm text-slate-500 mb-3">{t('auth.smsAccess')}</p>
            <Field label={t('auth.phone')}>
              <input className={inputClass} type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+225…" required disabled={codeSent} />
            </Field>
            {codeSent && (
              <Field label={t('auth.codeReceived')}>
                <input className={inputClass} value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" autoFocus />
              </Field>
            )}
            {info && <p className="text-slate-500 text-sm mb-2">{info}</p>}
            {error && <p className="text-danger text-sm mb-3">{error}</p>}
            <Button type="submit" disabled={busy} className="w-full">
              {busy ? '…' : codeSent ? t('auth.verifyCode') : t('auth.sendCode')}
            </Button>
            {codeSent && (
              <button type="button" onClick={() => { setCodeSent(false); setCode(''); setInfo(''); }} className="w-full text-xs text-slate-400 mt-2 underline">
                {t('auth.changeNumber')}
              </button>
            )}
          </form>
        )}

        <p className="text-xs text-slate-400 mt-4">
          {t('auth.disclaimer')}
        </p>
      </div>
    </div>
  );
}
